import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, ArrowRight, Bookmark, CalendarDays, Link2, Download } from "lucide-react";
import { useAdminMode } from "../../lib/AdminModeContext";
import { TeamBadge } from "../TeamBadge";
import { PlayerAvatar } from "../PlayerAvatar";
import { teamColor } from "../../lib/teamMap";
import { STORY_TEMPLATES } from "../../lib/storyTemplates";
import { StoryPoster } from "./StoryPoster";
import { ElementsOverlay } from "./PosterElements";
import { GAME_LAYOUTS } from "./GameTemplates";
import { ContractV2Card } from "./ContractV2Skins";
import { resolveStory, overrideFor, v1KeyForItem } from "../../lib/storyResolve";
import { useCreatorMode } from "../../lib/CreatorModeContext";
import { ensureStoryDesk } from "../../lib/storyDesk";

const byKey = Object.fromEntries(STORY_TEMPLATES.map((t) => [t.key, t]));

// Real template pools from the Templates page, split by orientation so the public
// feed can mix genuine templates in with the basic story cards.
const V_TPLS = STORY_TEMPLATES.filter((t) => !t.horizontalOnly);
const H_TPLS = STORY_TEMPLATES.filter((t) => !t.verticalOnly);

const initialsOf = (s = "") => { const p = s.trim().split(/\s+/).filter(Boolean); return (p.length > 1 ? p[0][0] + p[p.length - 1][0] : s.slice(0, 2)).toUpperCase(); };

// Player circle with a team-coloured ring. Uses the player's EDITED (cropped)
// headshot via PlayerAvatar (looked up by playerId) — the same framing shown on
// profile pages — never the raw file. Team subjects show the crest.
function Face({ subject, color, px = 104, crestStyle = "ring" }) {
    const isPlayer = subject && subject.kind !== "team";
    if (!isPlayer) {
        // Team crest — style controlled by the "Circle logo" Creator toggle.
        if (crestStyle === "logo") return <div className="shrink-0 self-center flex items-center justify-center" style={{ width: px, height: px }} data-testid="story-face">{subject?.team_id ? <TeamBadge teamId={subject.team_id} size="xl" asLink={false} /> : <span className="font-story-punch text-2xl text-white/70">{initialsOf(subject?.name)}</span>}</div>;
        const ring = crestStyle === "ring";
        return (
            <div className="shrink-0 self-center rounded-full" style={ring ? { padding: 3, background: `linear-gradient(160deg, ${color}, ${color}44)`, width: px + 6, height: px + 6 } : { width: px, height: px }} data-testid="story-face">
                <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center" style={{ width: px, height: px }}>
                    {subject?.team_id ? <TeamBadge teamId={subject.team_id} size="xl" asLink={false} /> : <span className="font-story-punch text-2xl text-white/70">{initialsOf(subject?.name)}</span>}
                </div>
            </div>
        );
    }
    return (
        <div className="shrink-0 self-center rounded-full p-[3px]" style={{ background: `linear-gradient(160deg, ${color}, ${color}44)`, width: px + 6, height: px + 6 }} data-testid="story-face">
            <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center" style={{ width: px, height: px }}>
                <PlayerAvatar photo={subject?.photo_url} name={subject?.name} playerId={subject?.id || ""} sizePx={px} showTierRing={false} />
            </div>
        </div>
    );
}

const Pill = ({ label, color, live, light }) => (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] sm:text-[10px] font-black uppercase tracking-wider"
        style={{ color: light ? color : color, background: `${color}${light ? "14" : "1a"}`, border: `1px solid ${color}59` }} data-testid="story-type-pill">
        {live ? <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: color }} /><span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: color }} /></span>
            : <Bookmark className="h-2.5 w-2.5" />}
        {label}
    </span>
);

function MilestoneDetail({ d, color }) {
    const pct = Math.max(4, Math.min(100, Math.round((d.current / (d.target || 1)) * 100)));
    return (
        <div className="relative mt-4 pt-3 border-t border-white/10 space-y-2 fade-up" data-testid="story-milestone-detail">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-white/55">
                <span>{d.live ? "Milestone reached" : `${d.away} ${d.statLabel} to go`}</span>
                <span className="tabular-nums text-white">{d.current} / {d.target} {d.statLabel}</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, #FF7A18)` }} /></div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-white/45">
                <span className="rounded px-1.5 py-0.5 border border-white/10">Tier {d.tier}</span>
                <Link to="/milestones" className="ml-auto inline-flex items-center gap-1 hover:gap-1.5 transition-all" style={{ color }} data-testid="story-milestone-link">Milestones <ArrowRight className="h-3 w-3" /></Link>
            </div>
        </div>
    );
}

// Footer (team badges + time/live + more/expand).
function Footer({ s, moreColor, open, setOpen, testId, light, footerExtra }) {
    const sub = light ? "text-black/55" : "text-white/55";
    const strong = light ? "text-black/80" : "text-white/75";
    return (
        <div className={`relative mt-4 pt-3 border-t ${light ? "border-black/10" : "border-white/[0.07]"} flex items-center justify-between gap-2`}>
            <div className={`flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider ${sub} min-w-0`}>
                {s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}
                <span className={strong}>{(s.teamId || (s.source || "")).toUpperCase()}</span>
                {s.oppId && s.oppId !== s.teamId && <><span className="opacity-40">vs</span><TeamBadge teamId={s.oppId} size="xs" asLink={false} /><span className={strong}>{(s.oppId || "").toUpperCase()}</span></>}
                {s.liveClock
                    ? <><span className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-black" style={{ color: "#A78BFA", border: "1px solid #A78BFA59" }}>LIVE</span><span className="opacity-40">·</span><span className={`${sub} tabular-nums`}>{s.liveClock}</span></>
                    : <><span className="opacity-40">·</span><span className={light ? "text-black/45" : "text-white/45"}>{s.ago}</span></>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
            {footerExtra}
            {s.expandable ? (
                <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testId}-toggle`} className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider shrink-0" style={{ color: moreColor }}>
                    {open ? "Less" : "More"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
            ) : !footerExtra ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider shrink-0 group-hover:gap-1.5 transition-all" style={{ color: moreColor }}>More <ArrowRight className="h-3.5 w-3.5" /></span>
            ) : null}
            </div>
        </div>
    );
}

// One story card. orientation: "h" (horizontal) | "v" (vertical 4:5). variant: "dark" | "white".
export function StoryFeedCard({ s, testId, orientation = "h", variant = "dark", square = false, hideEls = [], footerExtra = null }) {
    const [open, setOpen] = useState(false);
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || "#9D4CDD";
    // Footer "More" accent MATCHES the card's main colour (team / season-stage)
    // — never a random per-type colour.
    const moreColor = color;
    const white = variant === "white" || s.variant === "white";
    // Gradient uses ONLY the team colour over a neutral near-black (no green tint).
    const cardBg = white
        ? { background: "#F4F3EE" }
        : { background: `radial-gradient(130% 150% at 82% 8%, ${color}2b 0%, transparent 52%), linear-gradient(160deg, #101114 0%, #08090B 100%)` };
    const border = white ? "border-black/10" : "border-white/10 hover:border-white/20";
    const line1Cls = white ? "text-black" : "text-white";
    const line2Cls = white ? "text-black" : "text-white";
    const blurbCls = white ? "text-black/60" : "text-white/65";
    const slim = orientation === "slim";      // slim horizontal banner
    const slimV = orientation === "slimv";     // slim vertical
    const vertical = orientation === "v" || slimV;
    const textPos = s._pos || "top";
    const line1Caps = s._line1Case === "caps";
    const line1Case = line1Caps ? "uppercase" : "";
    const textPosX = s._posX || "left";
    const alignX = textPosX === "center" ? "items-center text-center" : textPosX === "right" ? "items-end text-right" : "items-start text-left";
    const hasFace = !!s.subject;   // team cards now show their crest, like the player photo

    // Faded team crest watermark. Position (9 anchors incl. centre) + scale are
    // Creator-controlled (`_wmPos` / `_wmScale`); scaling grows FROM the anchor,
    // so a centred watermark expands in ALL directions (full-screen at high scale).
    const wmPos = s._wmPos || "mr";
    const wmScale = Number(s._wmScale || 1) || 1;
    const wmOpacity = s._wmOpacity != null ? Number(s._wmOpacity) : (white ? 0.06 : 0.08);
    const WM_POS = {
        tl: ["items-start", "justify-start", "left top"], tc: ["items-start", "justify-center", "center top"], tr: ["items-start", "justify-end", "right top"],
        ml: ["items-center", "justify-start", "left center"], mc: ["items-center", "justify-center", "center center"], mr: ["items-center", "justify-end", "right center"],
        bl: ["items-end", "justify-start", "left bottom"], bc: ["items-end", "justify-center", "center bottom"], br: ["items-end", "justify-end", "right bottom"],
    };
    const [wmAlignY, wmAlignX, wmOrigin] = WM_POS[wmPos] || WM_POS.mr;
    const wmBase = (vertical ? 2.2 : 1.9) * wmScale;
    const Watermark = () => (
        <div className={`pointer-events-none absolute inset-0 flex ${wmAlignY} ${wmAlignX} p-3`} style={{ opacity: wmOpacity }} aria-hidden="true">
            {s.teamId && <div style={{ transform: `scale(${wmBase})`, transformOrigin: wmOrigin }}><TeamBadge teamId={s.teamId} size="xl" asLink={false} /></div>}
        </div>
    );
    const Pills = () => <div className="flex flex-wrap items-center gap-1.5 mb-2.5">{(s.pills || []).map((p, i) => <Pill key={i} label={p} color={moreColor} live={p.toLowerCase() === "live"} light={white} />)}</div>;
    // Optional team-logo (Creator): "logo" = crest only, "black" = crest on a
    // black circle, "ring" = black circle with team-colour ring. Top-left, mirrors the player circle.
    const CrestCircle = ({ px = 88 }) => {
        const style = s._crest;
        if (!s.teamId) return null;
        if (style === "logo") return <div className="shrink-0 flex items-center justify-center" style={{ width: px, height: px }} data-testid="story-crest-circle"><TeamBadge teamId={s.teamId} size="xl" asLink={false} /></div>;
        const ring = style === "ring";
        return (
            <div className="shrink-0 rounded-full" style={ring ? { padding: 3, background: `linear-gradient(160deg, ${color}, ${color}44)`, width: px + 6, height: px + 6 } : { width: px, height: px }} data-testid="story-crest-circle">
                <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center" style={{ width: px, height: px }}>
                    <TeamBadge teamId={s.teamId} size="xl" asLink={false} />
                </div>
            </div>
        );
    };
    const Head = ({ big }) => (<>
        {show("line1") && <h3 className={`font-story-serif leading-[0.9] ${line1Case} ${line1Cls} ${big ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`} data-testid="story-line1">{s.line1}</h3>}
        {show("line2") && <h2 className={`font-story-punch uppercase leading-[0.86] ${line2Cls} ${big ? "text-3xl sm:text-5xl" : "text-2xl sm:text-4xl"} mt-0.5`} data-testid="story-line2">{s.line2}</h2>}
    </>);
    const Spacer = () => <div className="flex-1" aria-hidden="true" />;
    const hJustify = textPos === "top" ? "justify-start" : textPos === "bottom" ? "justify-end" : "justify-center";

    // Slim horizontal = a genuinely thin banner: small face, one-line head/sub.
    if (slim) return (
        <article className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-xl"} border ${border} transition-colors px-4 py-3 shadow-lg shadow-black/40 flex items-center gap-3`} style={cardBg} data-testid={testId} data-orientation={orientation}>
            {show("watermark") && <Watermark />}
            {show("face") && hasFace && <Face subject={s.subject} color={color} px={46} crestStyle={s._faceStyle || "ring"} />}
            <div className="relative min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                    {s._crest && s.teamId ? <CrestCircle px={26} /> : (show("badge") && s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />)}
                    {show("pills") && (s.pills || []).slice(0, 1).map((p, i) => <Pill key={i} label={p} color={moreColor} live={p.toLowerCase() === "live"} light={white} />)}
                </div>
                {show("line1") && <div className={`font-story-serif leading-tight text-[13px] truncate ${line1Case} ${line1Cls}`} data-testid="story-line1">{s.line1}</div>}
                {show("line2") && <div className={`font-story-punch uppercase leading-none text-lg sm:text-xl truncate ${line2Cls}`} data-testid="story-line2">{s.line2}</div>}
            </div>
            {show("footer") && <span className="relative shrink-0 text-[9px] font-black uppercase tracking-widest" style={{ color: moreColor }}>More →</span>}
        </article>
    );

    return (
        <article className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border ${border} transition-colors p-5 sm:p-6 shadow-xl shadow-black/40 flex flex-col ${open && s.expandable ? "" : vertical ? (slimV ? "aspect-[9/16]" : "aspect-[4/5]") : "h-full"}`} style={cardBg} data-testid={testId} data-orientation={orientation}>
            {show("watermark") && <Watermark />}
            {vertical ? (
                <div className="relative flex flex-col flex-1 min-h-0">
                    <div className="flex items-start justify-between gap-2">
                        {s._crest && s.teamId ? <CrestCircle px={96} /> : (show("badge") && s.teamId ? <TeamBadge teamId={s.teamId} size="sm" asLink={false} /> : <span />)}
                        {show("face") && hasFace && <Face subject={s.subject} color={color} px={96} crestStyle={s._faceStyle || "ring"} />}
                    </div>
                    {(textPos === "center" || textPos === "bottom") && <Spacer />}
                    <div className={`mt-3 flex flex-col ${alignX}`}>
                        {show("pills") && <Pills />}
                        <Head big />
                        {show("blurb") && <p data-story-blurb className={`mt-2.5 text-[13px] leading-snug line-clamp-3 ${blurbCls}`}>{s.blurb}</p>}
                        {s.expandable && open && <MilestoneDetail d={s.detail} color={color} />}
                    </div>
                    {textPos === "center" && <Spacer />}
                    <div className={textPos === "top" ? "mt-auto pt-2" : "pt-2"}>{show("footer") && <Footer s={s} moreColor={moreColor} open={open} setOpen={setOpen} testId={testId} light={white} footerExtra={footerExtra} />}</div>
                </div>
            ) : (
                <>
                    <div className="relative flex items-stretch gap-3 sm:gap-4 flex-1 min-h-0">
                        <div className={`min-w-0 flex-1 flex flex-col ${hJustify} ${alignX}`}>
                            {s._crest && s.teamId ? <div className="mb-2.5"><CrestCircle px={60} /></div> : (show("badge") && s.teamId && <div className="mb-2.5"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>)}
                            {show("pills") && <Pills />}
                            <Head />
                            {show("blurb") && <p data-story-blurb className={`mt-2.5 text-[12px] sm:text-[13px] leading-snug ${slim ? "line-clamp-1" : "line-clamp-2"} max-w-md ${blurbCls}`}>{s.blurb}</p>}
                        </div>
                        {show("face") && hasFace && <Face subject={s.subject} color={color} px={slim ? 76 : 104} crestStyle={s._faceStyle || "ring"} />}
                    </div>
                    {s.expandable && open && <MilestoneDetail d={s.detail} color={color} />}
                    {!slim && <div className="mt-auto pt-2">{show("footer") && <Footer s={s} moreColor={moreColor} open={open} setOpen={setOpen} testId={testId} light={white} footerExtra={footerExtra} />}</div>}
                </>
            )}
        </article>
    );
}

// Remap a story into a chosen team's identity (colour + crest) — retained for
// callers that still want a straight recolour (unused by the feed today).
function remapTeam(s, tid) {
    if (!tid) return s;
    const c = teamColor(tid);
    const out = { ...s, teamId: tid, teamColor: c };
    if (s.subject) out.subject = { ...s.subject, team_id: tid, teamColor: c };
    if (s.oppId === tid) out.oppId = null;
    return out;
}

// SLIDES — Instagram-style paged story card (4:5). Each slide is BUILT FROM an
// existing template (via `tplKey`) or an inline story override. The paging
// arrow sits in the footer (single control — no duplicate "More").
export function SlidesCard({ s, testId, hideEls = [], square = false, variant = "dark", idx = null, studio = null, schedule = null }) {
    const [i, setI] = useState(0);
    const slides = (s.slides && s.slides.length) ? s.slides : [{}];
    const cur = i % slides.length;
    const slide = slides[cur];
    const color = s.teamColor || "#9D4CDD";
    // A slide is just an existing template entity: resolve THAT template's saved
    // edits (per-format, format "v") so a change made anywhere reflects here too.
    // A slide's DESIGN template key is derived by the SAME function the public
    // feed uses (`v1KeyForItem`), so a slide inside a deck maps to the exact same
    // template the gallery edits — game_vs (result vs preview by score), slides,
    // game layouts, feed types — guaranteeing gallery edits land on this card.
    const slideKey = slide.v1Key || (slide.tplKey ? (byKey[slide.tplKey]?.v1Key || slide.tplKey)
        : v1KeyForItem({ tpl: { layout: slide.layout, feedCard: slide.layout === "feedcard" }, s: slide.story, type: slide.story?.type }));
    const linked = !!(studio && slideKey);
    const ov = linked ? overrideFor(studio, slideKey, "v") : null;
    const slideHide = linked ? ov.hideEls : hideEls;
    const ovRest = linked ? ov.rest : {};
    // A slide is a COMBINED template, not a new version — so the footer/paging
    // accent must match THIS slide's own template colour, not the deck's.
    const curStory = slide.item ? slide.item.s
        : (slide.story ? slide.story
            : (slide.tplKey && byKey[slide.tplKey] ? (idx ? resolveStory(byKey[slide.tplKey], idx) : byKey[slide.tplKey].story) : null));
    const slideColor = (curStory && (curStory.teamColor || (curStory.teamId ? teamColor(curStory.teamId) : null))) || color;
    // The paging 'Next' colour matches THIS slide's own template exactly — so the
    // penalty PBP slide (forced red) shows a red Next, every other slide its own.
    const navColor = (curStory && curStory._pbpKind === "penalty") ? "#EF4444" : slideColor;
    const total = slides.length;
    // Windowed dots: never more than 3, sliding around the current slide, with a
    // compact "n/N" counter and ‹ › arrows (‹ · · · › + 1/5).
    const dotIdxs = total <= 3 ? slides.map((_, k) => k)
        : [0, 1, 2].map((d) => Math.max(0, Math.min(cur - 1, total - 3)) + d);
    const nav = total <= 1 ? null : (
        <span className="inline-flex items-center gap-1.5" data-testid="slides-nav">
            <button type="button" data-testid="slides-prev" aria-label="Previous slide"
                onClick={(e) => { e.stopPropagation(); setI((cur - 1 + total) % total); }}
                className="inline-flex items-center" style={{ color: navColor }}>
                <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="flex items-center gap-1">
                {dotIdxs.map((k) => <span key={k} className="h-1.5 rounded-full transition-all" style={{ background: k === cur ? navColor : "rgba(255,255,255,0.3)", width: k === cur ? 10 : 6 }} />)}
            </span>
            <button type="button" data-testid="slides-next" aria-label="Next slide"
                onClick={(e) => { e.stopPropagation(); setI((cur + 1) % total); }}
                className="inline-flex items-center" style={{ color: navColor }}>
                <ChevronRight className="h-4 w-4" />
            </button>
            <span className="text-[10px] font-black tabular-nums ml-0.5" style={{ color: navColor }} data-testid="slides-counter">{cur + 1}/{total}</span>
        </span>
    );
    if (slide.item) {
        const it = slide.item;
        const withC = { ...it.s, teamColor: it.s?.teamColor || (it.s?.teamId ? teamColor(it.s.teamId) : color) };
        if (it.tpl?.layout && GAME_LAYOUTS[it.tpl.layout]) {
            const G = GAME_LAYOUTS[it.tpl.layout];
            return <G s={withC} orientation="v" square={square} hideEls={it.hideEls || []} testId={testId} footerExtra={nav} schedule={schedule} />;
        }
        if (it.tpl?.feedCard || it.tpl?.layout === "feedcard") {
            return <StoryFeedCard s={withC} orientation="v" variant={it.tpl?.variant} testId={testId} square={square} hideEls={it.hideEls || []} footerExtra={nav} />;
        }
        return <StoryFeedCard s={withC} orientation="v" testId={testId} square={square} hideEls={it.hideEls || []} footerExtra={nav} />;
    }
    if (slide.layout && GAME_LAYOUTS[slide.layout]) {
        const GameCard = GAME_LAYOUTS[slide.layout];
        const story = { teamColor: s.teamColor, ...slide.story, ...ovRest };
        return <div className="relative" data-testid={testId}><GameCard s={story} orientation="v" square={square} hideEls={slideHide} footerExtra={nav} /></div>;
    }
    if (slide.tplKey && byKey[slide.tplKey]) {
        const tpl = byKey[slide.tplKey];
        const base = idx ? resolveStory(tpl, idx) : { ...tpl.story };
        const story = { ...base, ...(slide.override || {}), ...ovRest, teamColor: base.teamColor || (base.teamId ? teamColor(base.teamId) : color) };
        if (GAME_LAYOUTS[tpl.layout]) {
            const G = GAME_LAYOUTS[tpl.layout];
            return <G s={story} orientation="v" square={square} hideEls={slideHide} testId={testId} footerExtra={nav} />;
        }
        return <StoryFeedCard s={story} orientation="v" variant={tpl.variant} testId={testId} square={square} hideEls={slideHide} footerExtra={nav} />;
    }
    const merged = { ...s, ...slide, ...ovRest, slides: undefined, expandable: false };
    return <StoryFeedCard s={merged} orientation="v" variant={variant} testId={testId} square={square} hideEls={slideHide} footerExtra={nav} />;
}

// Dispatch a template to its renderer: feed-card templates use the newspaper
// StoryFeedCard; game templates use their dedicated designs; every other
// template uses its StoryPoster layout.
// A GROUP rendered as its members SIDE BY SIDE in the live template order (no
// shrinking into a deck). Optional group name shows as a header. Scrolls
// horizontally when the row is wider than the viewport.
function GroupRow({ s, testId, idx = null, studio = null, schedule = null }) {
    const items = (s && s.items) || [];
    const color = s?.teamColor || "#9D4CDD";
    return (
        <div data-testid={testId ? `${testId}-grouprow` : "story-group-row"}>
            {s?.groupName && (
                <div className="flex items-center gap-2 mb-2">
                    <span className="font-story-punch uppercase text-base tracking-tight text-white" style={{ color }} data-testid="group-row-name">{s.groupName}</span>
                    <span className="h-[2px] flex-1 rounded" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
                </div>
            )}
            <div className="flex gap-4 overflow-x-auto pb-2 items-start snap-x">
                {items.map((it, i) => (
                    <div key={i} className="w-[280px] sm:w-[320px] shrink-0 snap-start">
                        <GroupChild tpl={it.tpl} s={it.s} hideEls={it.hideEls} square={it.square} skin={it.skin} testId={`${testId}-g${i}`} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// Renders ONE grouped card by INLINE dispatch — deliberately NOT via
// StoryTemplateView, to avoid a StoryTemplateView ↔ GroupRow textual cycle that
// blows the visual-edits babel plugin's stack (same trick SlidesCard uses).
function GroupChild({ tpl, s, hideEls = [], square = false, skin = null, testId }) {
    const withColor = { ...s, teamColor: s?.teamColor || (s?.teamId ? teamColor(s.teamId) : "#9D4CDD") };
    if (tpl?.layout && GAME_LAYOUTS[tpl.layout]) {
        const G = GAME_LAYOUTS[tpl.layout];
        return <G s={withColor} orientation="v" square={square} hideEls={hideEls} testId={testId} />;
    }
    if (tpl?.feedCard || tpl?.layout === "feedcard") {
        if (skin && String(skin).endsWith("_v2")) return <ContractV2Card s={withColor} orientation="v" testId={testId} square={square} hideEls={hideEls} />;
        return <StoryFeedCard s={withColor} orientation="v" variant={tpl?.variant} testId={testId} square={square} hideEls={hideEls} />;
    }
    return (
        <div className={`relative overflow-hidden border border-white/10 ${square ? "rounded-none" : ""}`} data-testid={testId ? `${testId}-wrap` : undefined}>
            <StoryPoster s={withColor} layout={tpl?.layout} orientation="v" variant={tpl?.variant} testId={testId} />
            <ElementsOverlay els={withColor._els} />
        </div>
    );
}

// Every vertical card renders at ONE design width (= the 3.2× download layout) and is scaled
// to its slot, inside `.story-desk` (desktop styles on every screen) — same look on all devices.
const DESIGN_W = 338;
function FitStory({ ratio, children, testId }) {
    const ref = useRef(null);
    const inner = useRef(null);
    const [w, setW] = useState(0);
    const [h, setH] = useState(0);
    useEffect(() => {
        ensureStoryDesk();
        const el = ref.current; if (!el) return undefined;
        const ro = new ResizeObserver(() => { setW(el.clientWidth); if (inner.current) setH(inner.current.offsetHeight); });
        ro.observe(el); setW(el.clientWidth);
        return () => ro.disconnect();
    }, []);
    useEffect(() => {
        const el = inner.current; if (!el) return undefined;
        const ro = new ResizeObserver(() => setH(el.offsetHeight)); ro.observe(el); setH(el.offsetHeight);
        return () => ro.disconnect();
    }, [w]);
    const k = w / DESIGN_W;
    // Height follows the card's real height (slide decks have a header row), so nothing is cropped.
    return (
        <div ref={ref} className="story-desk relative w-full overflow-hidden" style={h ? { height: h * k } : { aspectRatio: ratio }} data-testid={testId ? `${testId}-fit` : undefined}>
            {w > 0 && <div ref={inner} style={{ width: DESIGN_W, transform: `scale(${k})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0 }}>{children}</div>}
        </div>
    );
}

export function StoryTemplateView(props) {
    const { tpl, orientation = "h", testId, noFit = false } = props;
    const vert = (orientation === "v" || orientation === "slimv") && tpl?.layout !== "group_row";
    if (noFit) { ensureStoryDesk(); return <div className="story-desk"><StoryTemplateInner {...props} /></div>; }
    if (!vert) return <StoryTemplateInner {...props} />;
    return <FitStory ratio={orientation === "slimv" ? "9 / 16" : "4 / 5"} testId={testId}><StoryTemplateInner {...props} /></FitStory>;
}

function StoryTemplateInner({ tpl, s, orientation = "h", testId, square = false, teamOverride = null, hideEls = [], idx = null, footerExtra = null, studio = null, schedule = null, skin = null }) {
    let story = s || tpl.story;
    if (teamOverride) story = remapTeam(story, teamOverride);
    // Persisted per-template SCALE (Creator "Scale" slider). Renders the card at a
    // wider/narrower internal width then transforms it back to fill the slot —
    // same zoom feel as the Download page, but SAVED. Vertical cards only.
    const rawScale = story && typeof story._scale === "number" && story._scale > 0 ? story._scale : 1;
    const canScale = rawScale !== 1 && (orientation === "v" || orientation === "slimv") && tpl.layout !== "slides" && tpl.layout !== "group_row";
    const wrapScale = (el) => !canScale ? el : (
        <div className={`relative aspect-[4/5] overflow-hidden ${square ? "" : "rounded-2xl border border-white/10"}`} data-testid={testId ? `${testId}-scalewrap` : undefined}>
            <div style={{ width: `${100 / rawScale}%`, transform: `scale(${rawScale})`, transformOrigin: "top left" }}>{el}</div>
        </div>
    );
    if (tpl.layout === "group_row") {
        return <GroupRow s={story} testId={testId} idx={idx} studio={studio} schedule={schedule} />;
    }
    if (tpl.layout === "slides") {
        const withColor = { ...story, teamColor: story.teamColor || (story.teamId ? teamColor(story.teamId) : "#9D4CDD") };
        return <SlidesCard s={withColor} variant={tpl.variant} testId={testId} square={square} hideEls={hideEls} idx={idx} studio={studio} schedule={schedule} />;
    }
    if (GAME_LAYOUTS[tpl.layout]) {
        const GameCard = GAME_LAYOUTS[tpl.layout];
        const withColor = { ...story, teamColor: story.teamColor || (story.teamId ? teamColor(story.teamId) : "#9D4CDD") };
        return wrapScale(<GameCard s={withColor} orientation={orientation} square={square} hideEls={hideEls} testId={testId} footerExtra={footerExtra} schedule={schedule} />);
    }
    if (tpl.feedCard || tpl.layout === "feedcard") {
        const withColor = { ...story, teamColor: story.teamColor || (story.teamId ? teamColor(story.teamId) : "#9D4CDD") };
        // Team style V2 skin — a bespoke redesign PER team (contract templates).
        if (skin && String(skin).endsWith("_v2")) return wrapScale(<ContractV2Card s={withColor} orientation={orientation} testId={testId} square={square} hideEls={hideEls} />);
        return wrapScale(<StoryFeedCard s={withColor} orientation={orientation} variant={tpl.variant} testId={testId} square={square} hideEls={hideEls} footerExtra={footerExtra} />);
    }
    return wrapScale(
        <div className={`relative overflow-hidden border border-white/10 ${square ? "rounded-none" : ""}`} data-testid={testId ? `${testId}-wrap` : undefined}>
            <StoryPoster s={story} layout={tpl.layout} orientation={orientation} variant={tpl.variant} testId={testId} />
            <ElementsOverlay els={story._els} />
        </div>
    );
}

const FMT_LABEL = { v: "4:5", h: "16:9", slim: "Slim (H)", slimv: "Slim (V)" };
const FMT_ORDER = ["v", "h", "slim", "slimv"];
const gridForFmt = (o) => o === "h" ? "grid-cols-1 lg:grid-cols-2" : o === "slim" ? "grid-cols-1 xl:grid-cols-2" : o === "slimv" ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-5" : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4";

export function StoryDayFeed({ days, idx = null, studio = null, schedule = null }) {
    const { ownerView } = useAdminMode();
    const navigate = useNavigate();
    // Click = instant download (3.2×, straight edges). Right-click = the manual download page.
    const goDownload = (it, manual = false) => {
        try { sessionStorage.setItem("rosterly_dl", JSON.stringify(it)); sessionStorage.setItem("rosterly_dl_auto", manual ? "0" : "1"); } catch { /* ignore */ }
        navigate("/stories/download");
    };
    if (!days?.length) return (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center" data-testid="story-feed-empty">
            <p className="text-sm text-white/40">No stories on this day — pick another day on the timeline.</p>
        </div>
    );
    return (
        <div className="space-y-8" data-testid="story-day-feed">
            {days.map((day) => (
                <section key={day.date} data-testid={`story-day-${day.date}`}>
                    <div className="flex items-center gap-2.5 mb-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#F5C518]/15 text-[#F5C518]"><CalendarDays className="h-4 w-4" /></span>
                        <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white" data-testid={`story-day-label-${day.date}`}>{day.label}</h2>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-white/35 tabular-nums">{day.items.length} {day.items.length === 1 ? "story" : "stories"}</span>
                        <span className="ml-1 h-px flex-1 bg-white/10" />
                    </div>

                    {FMT_ORDER.map((fmt) => {
                        const group = day.items.filter((it) => (it.orientation || "v") === fmt);
                        if (!group.length) return null;
                        return (
                            <div key={fmt} className="mb-6" data-testid={`story-day-fmt-${fmt}-${day.date}`}>
                                <div className={`grid ${gridForFmt(fmt)} gap-4 items-start`}>
                                    {group.map((it, i) => (
                                        <div key={it.id} className={`relative ${it.tpl?.layout === "group_row" ? "sm:col-span-2 xl:col-span-4" : ""}`}>
                                            {ownerView && (
                                                <button onClick={() => goDownload(it)} onContextMenu={(e) => { e.preventDefault(); goDownload(it, true); }} data-testid={`download-story-${day.date}-${fmt}${i}`}
                                                    title="Download (3.2× · straight edges) — right-click for manual options"
                                                    className="absolute top-2 right-2 z-20 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-[#B366F0] hover:border-[#B366F0] hover:text-black transition-colors shadow-lg">
                                                    <Download className="h-4 w-4" />
                                                </button>
                                            )}
                                            <StoryTemplateView tpl={it.tpl} s={it.sourceRef ? { ...it.s, sourceRef: it.sourceRef } : it.s} orientation={it.orientation} hideEls={it.hideEls} square={it.square} skin={it.skin} idx={idx} studio={studio} schedule={schedule} testId={`feed-template-${day.date}-${fmt}${i}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </section>
            ))}
        </div>
    );
}
